import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Select, Table, Modal, message } from "antd";
import okilogo from "../../assets/OKI-logo.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { DatePicker, Space } from "antd";
import { getDetailedLeaveCounts, getAllLeaveReportData } from "../../service/adminAttendanceService";

const { RangePicker } = DatePicker;

const LeaveReport = () => {
  const startOfMonth = dayjs().startOf("month");
  const today = dayjs();
  const [dataSource, setDataSource] = useState([]);
  const [fullDataSource, setFullDataSource] = useState([]); // For downloads
  const [epf_number, setEpf] = useState("");
  const [name, setName] = useState("");
  const [branch, setBranch] = useState(null);
  const [center, setCenter] = useState(null);
  const [startDate, setStartDate] = useState(startOfMonth.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const [loading, setLoading] = useState(false);

  const branchOptions = [
    { value: "wattala", label: "Wattala" },
    { value: "negombo", label: "Negombo" },
    { value: "kaduwela", label: "Kaduwela" },
    { value: "kiribathgoda", label: "Kiribathgoda" },
    { value: "kandana", label: "Kandana" },
    { value: "delgoda", label: "Delgoda" },
    { value: "lsu", label: "Wattala LSU" },
    { value: "cityschool", label: "City School - Negombo" }
  ];

  const centerOptions = [
    { value: "OKI", label: "OKI" },
    { value: "OKIDS", label: "OKIDS" },
    { value: "OKIT", label: "OKIT" },
  ];

  const onChange = (dates, dateStrings) => {
    setStartDate(dateStrings[0]);
    setEndDate(dateStrings[1]);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    getRecords(newPagination.current, newPagination.pageSize);
  };

  const handlePageSizeChange = () => {
    const newPagination = { ...pagination, pageSize: tempPageSize, current: 1 };
    setPagination(newPagination);
    setPageSizeModalVisible(false);
  };

  const getRecords = async (pageNum = 1, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await getDetailedLeaveCounts({
        epf_number: epf_number || undefined,
        name: name || undefined,
        center: center || undefined,
        branch: branch || undefined,
        startDate,
        endDate,
        page: pageNum,
        limit: pageSize,
      });
      console.log("API Response:", response);
      const mappedData = Array.isArray(response.data)
        ? response.data.map((item) => ({
            id: item.employee_id,
            employee: {
              name: item.name || "-",
              epf_number: item.epf_number || "-",
              branch: item.branch || "-",
              employee_type: item.employee_type || "-",
              center: center || item.center || "N/A",
            },
            leave_counts: {
              regular: item.leave_counts?.regular || 0,
              short: item.leave_counts?.short || 0,
              special: item.leave_counts?.special || 0,
              duty: item.leave_counts?.duty || 0,
              maternity: item.leave_counts?.maternity || 0,
              roster: item.leave_counts?.["roster leave"] || 0,
              casual: item.leave_counts?.["casual leave"] || 0,
              annual: item.leave_counts?.["annual leave"] || 0,
              no_pay: item.leave_counts?.no_pay || 0,
              authorized_no_pay: item.leave_counts?.authorized_no_pay || 0,
            },
            leave_balances: {
              casual: item.leave_balances?.["casual leave"] || {
                currentBalance: 0,
                totalLeaves: 0,
                takenLeaves: 0,
              },
              annual: item.leave_balances?.["annual leave"] || {
                currentBalance: 0,
                totalLeaves: 0,
                takenLeaves: 0,
              },
            },
            total_leaves: item.total_leaves || 0,
            total_taken_leaves: item.total_taken_leaves || 0,
            leave_balance_start_date: item.leave_balance_start_date || 0,
            leave_balance_end_date: item.leave_balance_end_date || 0,
          }))
        : [];
      setDataSource(mappedData);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || mappedData.length,
        current: pageNum,
      }));
      setLoading(false);
      if (mappedData.length === 0) {
        message.warning("No leave records found for the selected filters.");
      }
    } catch (error) {
      console.error("Error fetching leave counts:", error);
      setLoading(false);
      message.error("Failed to fetch leave records. Please try again.");
    }
  };

  // useEffect(() => {
  //   getRecords();
  // }, [startDate, endDate, epf_number, name, branch, center]);

  // Fetch all data for downloads (without pagination)
  const fetchAllForDownload = async () => {
    if (fullDataSource.length > 0) return fullDataSource;
    const response = await getDetailedLeaveCounts({
      epf_number: epf_number || undefined,
      name: name || undefined,
      center: center || undefined,
      branch: branch || undefined,
      startDate,
      endDate,
    });
    const mappedData = Array.isArray(response.data)
      ? response.data.map((item) => ({
          id: item.employee_id,
          employee: {
            name: item.name || "-",
            epf_number: item.epf_number || "-",
            branch: item.branch || "-",
            employee_type: item.employee_type || "-",
            center: center || item.center || "N/A",
          },
          leave_counts: {
            regular: item.leave_counts?.regular || 0,
            short: item.leave_counts?.short || 0,
            special: item.leave_counts?.special || 0,
            duty: item.leave_counts?.duty || 0,
            maternity: item.leave_counts?.maternity || 0,
            roster: item.leave_counts?.["roster leave"] || 0,
            casual: item.leave_counts?.["casual leave"] || 0,
            annual: item.leave_counts?.["annual leave"] || 0,
            no_pay: item.leave_counts?.no_pay || 0,
            authorized_no_pay: item.leave_counts?.authorized_no_pay || 0,
          },
          leave_balances: {
            casual: item.leave_balances?.["casual leave"] || { currentBalance: 0, totalLeaves: 0, takenLeaves: 0 },
            annual: item.leave_balances?.["annual leave"] || { currentBalance: 0, totalLeaves: 0, takenLeaves: 0 },
          },
          total_leaves: item.total_leaves || 0,
          total_taken_leaves: item.total_taken_leaves || 0,
          leave_balance_start_date: item.leave_balance_start_date || 0,
          leave_balance_end_date: item.leave_balance_end_date || 0,
        }))
      : [];
    setFullDataSource(mappedData);
    return mappedData;
  };

  const handleSearch = () => {
    setFullDataSource([]); // Clear cached download data on new search
    setPagination((prev) => ({ ...prev, current: 1 }));
    getRecords(1, pagination.pageSize);
  };

  const handlePdfExport = async () => {
    setLoading(true);
    const exportData = await fetchAllForDownload();
    const doc = new jsPDF();
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text("OKI International", 15, 13);
    doc.setFontSize(14);
    doc.setTextColor(138, 132, 125);
    doc.text("Employees Leave Report", 15, 23);
    doc.text(`Date: ${startDate} - ${endDate}`, 15, 30);
    doc.addImage(okilogo, "PNG", 170, 8, 22, 25);

    autoTable(doc, {
      head: [
        [
          "Emp Name",
          "EPF No",
          "Branch",
          "Employee Type",
          "Short",
          "Special",
          "Duty",
          "Roster",
          "Casual",
          "Annual",
          "No Pay",
          { content: "Balance", styles: { fillColor: [144, 238, 144] } }, // Light Green
          { content: "Taken", styles: { fillColor: [255, 182, 193] } }, // Light Pink
          { content: "Total", styles: { fillColor: [173, 216, 230] } }, // Light Blue
        ],
      ],
      body: exportData.map(
        ({
          employee,
          leave_counts,
          leave_balances,
          total_leaves,
          total_taken_leaves,
        }) => [
          employee.name,
          employee.epf_number,
          employee.branch,
          employee.employee_type,
          leave_counts.short,
          leave_counts.special,
                    leave_counts.maternity,

          leave_counts.duty,
          leave_counts.roster,
          leave_counts.casual,
          leave_counts.annual,
          leave_counts.no_pay,
          (leave_balances.casual.currentBalance || 0) +
            (leave_balances.annual.currentBalance || 0),
          total_taken_leaves,
          total_leaves,
        ]
      ),
      startY: 35,
      theme: "striped",
      headStyles: {
        fillColor: [138, 132, 125],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        11: { fillColor: [144, 238, 144] }, // Balance column: Light Green
        12: { fillColor: [255, 182, 193] }, // Taken column: Light Pink
        13: { fillColor: [173, 216, 230] }, // Total column: Light Blue
      },
    });
    doc.save("leave_report.pdf");
    setLoading(false);
  };

const handleExcelExport = async () => {
  try {
    setLoading(true); // Show loading indicator
    const exportData = await fetchAllForDownload();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Leave Report");

    // Set default row height and column widths to match the original
    worksheet.properties.defaultRowHeight = 40;
    worksheet.properties.defaultColWidth = 10;

    // Freeze the header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    const borderStyle = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };

    const headerStyle = {
      font: { bold: true, size: 10, color: { argb: "FF000000" } },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // Light gray background for headers
      },
      alignment: {
        vertical: "middle",
        horizontal: "center",
        textRotation: 90,
        wrapText: true,
      },
      border: borderStyle,
    };

    // Format dates for column headers
    const formattedStartDate = dayjs(startDate).format("DD.MM.YYYY");
    const formattedEndDate = dayjs(endDate).format("DD.MM.YYYY");

    // Define columns with specific widths
    worksheet.columns = [
      { header: "EPF NUMBER", key: "epf_number", width: 12 },
      { header: "Name", key: "name", width: 35 },
      { header: "Branch", key: "branch", width: 20 },
      { header: "Employee Type", key: "employee_type", width: 15 },
      { header: `Balance as ${formattedStartDate}`, key: "balance_start_date", width: 12 },
      { header: "Total", key: "total", width: 12 },
      { header: "Total Taken", key: "total_taken", width: 12 },
      { header: "Casual", key: "casual", width: 10 },
      { header: "Annual", key: "annual", width: 10 },
      { header: "Roster", key: "roster", width: 10 },
      { header: "Short", key: "short", width: 10 },
      { header: "Duty", key: "duty", width: 10 },
      { header: "Special", key: "special", width: 10 },
             { header: "Maternity", key: "maternity", width: 10 },
      { header: "No Pay (Unauthorized)", key: "no_pay", width: 12 },
            { header: "No Pay (Authorized)", key: "authorized_no_pay", width: 12 },

      { header: `Balance as ${formattedEndDate}`, key: "balance_end_date", width: 12 },
    ];

    // Apply header styles with vertical text and specific fill colors
    const headerRow = worksheet.getRow(1);
    headerRow.height = 80; // Match original header row height
    headerRow.eachCell((cell, colNumber) => {
      cell.style = { ...headerStyle };
      if (colNumber >= 1 && colNumber <= 4) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }; // White for columns 1-4
      } else if (colNumber === 5) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "ADD8E6" } }; // Light blue for balance start
      } else if (colNumber === 6) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FBCEB1" } }; // Light orange for total
      } else if (colNumber === 7) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }; // White for total taken
      } else if (colNumber >= 8 && colNumber <= 10) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD3D3D3" } }; // Gray for casual, annual, roster
      } else if (colNumber >= 11 && colNumber <= 14) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "AFE1AF" } }; // Light green for short, duty, special
      } else if (colNumber === 15 && colNumber === 16) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF99" } }; // Yellow for no pay
      } else if (colNumber === 17) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "ADD8E6" } }; // Light blue for balance end
      }
    });

    // Add employee data from exportData
    exportData.forEach((item) => {
      const row = worksheet.addRow({
        epf_number: item.employee?.epf_number || "-",
        name: item.employee?.name || "-",
        branch: item.employee?.branch || "-",
        employee_type: item.employee?.employee_type || "-",
        balance_start_date: item.leave_balance_start_date|| 0,
        total: item.total_leaves || 0,
        total_taken: item.total_taken_leaves || 0,
        casual: item.leave_counts?.casual || 0,
        annual: item.leave_counts?.annual || 0,
        roster: item.leave_counts?.roster || 0,
        short: item.leave_counts?.short || 0,
        duty: item.leave_counts?.duty || 0,
        special: item.leave_counts?.special || 0,
        maternity: item.leave_counts?.maternity || 0,
        no_pay: item.leave_counts?.no_pay || 0,
        authorized_no_pay: item.leave_counts?.authorized_no_pay || 0,
        balance_end_date: item.leave_balance_end_date|| 0,
      });

      // Apply borders and alignment to data cells
      row.eachCell((cell) => {
        cell.border = borderStyle;
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      });
    });

    // ----------------------------------------------------
    // Create Sheet 2: Leave Breakdown
    // ----------------------------------------------------
    let detailedRecords = [];
    try {
      const detailedResponse = await getAllLeaveReportData({
        epf_number: epf_number || undefined,
        name: name || undefined,
        center: center || undefined,
        branch: branch || undefined,
        startDate,
        endDate,
      });
      if (Array.isArray(detailedResponse?.breakdown) && detailedResponse.breakdown.length > 0) {
        detailedRecords = detailedResponse.breakdown;
      } else if (Array.isArray(detailedResponse)) {
        detailedRecords = detailedResponse;
      } else if (Array.isArray(detailedResponse?.data)) {
        detailedRecords = detailedResponse.data;
      }
    } catch (err) {
      console.log("getAllLeaveReportData fallback:", err);
    }

    const breakdownWorksheet = workbook.addWorksheet("Leave Breakdown");

    breakdownWorksheet.properties.defaultRowHeight = 25;
    breakdownWorksheet.views = [{ state: "frozen", ySplit: 1 }];

    breakdownWorksheet.columns = [
      { header: "User ID", key: "user_id", width: 12 },
      { header: "EPF Number", key: "epf_number", width: 15 },
      { header: "Employee Name", key: "employee_name", width: 30 },
      { header: "Leave Date", key: "leave_date", width: 15 },
      { header: "Leave Type", key: "leave_type", width: 25 },
      { header: "Day Value", key: "day_value", width: 12 },
      { header: "Status", key: "status", width: 15 },
    ];

    const breakdownHeaderRow = breakdownWorksheet.getRow(1);
    breakdownHeaderRow.height = 30;
    breakdownHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, size: 10, color: { argb: "FF000000" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = borderStyle;
    });

    if (detailedRecords.length > 0) {
      detailedRecords.forEach((rec) => {
        const row = breakdownWorksheet.addRow({
          user_id: rec.user_id || rec.employee_id || rec.userId || rec.employee?.id || rec._id || "-",
          epf_number: rec.epf_number || rec.employee?.epf_number || "-",
          employee_name: rec.employee_name || rec.name || rec.employee?.name || "-",
          leave_date: rec.leave_date || rec.date || rec.startDate ? dayjs(rec.leave_date || rec.date || rec.startDate).format("YYYY-MM-DD") : "-",
          leave_type: rec.leave_type || rec.type || rec.leaveType || rec.leave_category || "-",
          day_value: rec.day_value ?? rec.dayValue ?? rec.value ?? rec.no_of_days ?? 1.0,
          status: rec.status || rec.leaveStatus || "Approved",
        });

        row.eachCell((cell) => {
          cell.border = borderStyle;
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        });
      });
    } else {
      // Fallback if exportData items contain nested leave breakdown
      exportData.forEach((item) => {
        const userId = item.id || item.employee_id || "-";
        const epfNumber = item.employee?.epf_number || "-";
        const employeeName = item.employee?.name || "-";
        const records = item.leave_breakdown || item.breakdown || item.leave_details || item.detailed_leaves || item.leaves || [];

        if (Array.isArray(records)) {
          records.forEach((rec) => {
            const row = breakdownWorksheet.addRow({
              user_id: userId,
              epf_number: epfNumber,
              employee_name: employeeName,
              leave_date: rec.leave_date || rec.date ? dayjs(rec.leave_date || rec.date).format("YYYY-MM-DD") : "-",
              leave_type: rec.leave_type || rec.type || rec.leaveType || "-",
              day_value: rec.day_value ?? rec.dayValue ?? rec.value ?? 1.0,
              status: rec.status || rec.leaveStatus || "Approved",
            });

            row.eachCell((cell) => {
              cell.border = borderStyle;
              cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            });
          });
        }
      });
    }

    // Apply autoFilter on all columns of Leave Breakdown sheet
    breakdownWorksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: 7 },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "detailed_leave_report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
    setLoading(false); // Hide loading indicator
  } catch (error) {
    setLoading(false);
    console.error("Error exporting to Excel:", error);
    message.error("Failed to export leave report to Excel. Please try again.");
  }
};

  const columns = [
    {
      title: "Emp Name",
      dataIndex: "employee",
      render: (employee) => employee?.name || "-",
    },
    {
      title: "EPF No",
      dataIndex: "employee",
      render: (employee) => employee?.epf_number || "-",
    },
    {
      title: "Branch",
      dataIndex: "employee",
      render: (employee) => employee?.branch || "-",
    },
    {
      title: "Employee Type",
      dataIndex: "employee",
      render: (employee) => employee?.employee_type || "-",
    },
    {
      title: "Short",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.short || 0,
    },
    {
      title: "Special",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.special || 0,
    },
    {
      title: "Duty",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.duty || 0,
    },
    {
      title: "Roster",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.roster || 0,
    },
    {
      title: "Casual",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.casual || 0,
    },
    {
      title: "Annual",
      dataIndex: "leave_counts",
      render: (leave_counts) => leave_counts?.annual || 0,
    },
    {
      title: "No Pay",
      dataIndex: "leave_counts",
      render: (leave_counts) => (leave_counts?.no_pay + leave_counts?.authorized_no_pay)|| 0,
    },
    {
      title: "Total",
      dataIndex: "total_taken_leaves",
      render: (total_taken_leaves) => total_taken_leaves || 0,
    },
  ];

  return (
    <div>
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Leave Report</h4>
                  <h6></h6>
                </div>
              </div>
              <div className="wordset">
                <ul>
                  <li>
                    <a title="pdf" onClick={handlePdfExport}>
                      <img src="assets/img/icons/pdf.svg" alt="pdf" />
                    </a>
                  </li>
                  <li>
                    <a title="pdf" onClick={handleExcelExport}>
                      <img src="assets/img/icons/excel.svg" alt="excel" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="card table-list-card-grid">
            <div
              className="card-body"
              style={{ maxWidth: "100%", marginRight: "20px" }}
            >
              <div
                className="container mt-3"
                style={{
                  maxWidth: "100%",
                  marginLeft: "20px",
                  marginRight: "40px",
                }}
              >
                <div className="row d-flex flex-wrap align-items-start justify-content-between">
                  <div className="col-12 col-md-3 text-start">
                    <label className="form-label">Employee Name</label>
                    <input
                      type="text"
                      value={name}
                      className="form-control"
                      onChange={(e) => setName(e.target.value)}
                      style={{ width: "100%", height: "38px" }}
                    />
                  </div>
                  <div className="col-12 col-md-2 mb-3 text-start">
                    <label className="form-label">EPF No</label>
                    <input
                      className="form-control"
                      type="text"
                      value={epf_number}
                      onChange={(e) => setEpf(e.target.value)}
                      style={{ width: "100%", height: "38px" }}
                    />
                  </div>
                  <div className="col-12 col-md-2 mb-3 text-start">
                    <label className="form-label">Branch</label>
                    <Select
                      value={branch}
                      options={branchOptions}
                      className="select"
                      placeholder="Select Branch"
                      onChange={setBranch}
                      allowClear
                      style={{ width: "100%", height: "38px" }}
                    />
                  </div>
                  <div className="col-12 col-md-2 mb-3 text-start">
                    <label className="form-label">Center</label>
                    <Select
                      value={center}
                      options={centerOptions}
                      className="select"
                      placeholder="Select Center"
                      onChange={setCenter}
                      allowClear
                      style={{ width: "100%", height: "38px" }}
                    />
                  </div>
                  <div className="col-12 col-md-3 mb-3 text-start">
                    <label className="form-label">Date</label>
                    <Space direction="vertical" size={12}>
                      <RangePicker
                        format="YYYY-MM-DD"
                        onChange={onChange}
                        defaultValue={[startOfMonth, today]}
                        style={{ width: "100%" }}
                      />
                    </Space>
                  </div>
                </div>
              </div>
              <div
                className="container mt-0"
                style={{
                  maxWidth: "100%",
                  marginLeft: "20px",
                  marginRight: "40px",
                }}
              >
                <div className="row d-flex align-items-center justify-content-start">
                  <div className="col-12 col-md-2 mb-3 text-start">
                    <button
                      disabled={loading}
                      className="btn btn-submit"
                      onClick={handleSearch}
                      style={{ height: "38px", width: "55%" }}
                    >
                      {loading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card table-list-card-grid">
            <div className="card-body pb-0">
              <div className="table-top">
                <div className="input-blocks search-set mb-0"></div>
              </div>
            </div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowKey={(record) => record.id}
                className="custom-ant-table-grid"
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  pageSize: pagination.pageSize,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                  onShowSizeChange: (current, size) => {
                    setPagination((prev) => ({
                      ...prev,
                      pageSize: size,
                      current: 1,
                    }));
                  },
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </button>,
          <button
            key="submit"
            className="btn btn-submit"
            onClick={handlePageSizeChange}
          >
            Apply
          </button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: "100%" }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default LeaveReport;
